import React, { createContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';

import { useNotification } from '@global/hooks/useNotification';
import { Spinner } from '@global/components/UI Elements/Spinner';

import analytic from '@global/utils/AdobeAnalytics';

import { CREATE_PAYMENT_INTENT } from '@global/requests/checkout/';
import type { CREATE_PAYMENT_INTENT_TYPE } from '@global/requests/checkout/';
import { NotificationTypes } from '@global/components/Notification';
import { paymentInfo, STATUSES } from '../../types';
import { useSelector } from 'react-redux';
import { cartSelector } from '@src/global/store/slices/cart/selectors';
import { useAppDispatch } from '@src/global/store/hooks';
import { updateWpsPaymentInformation } from '@src/global/store/slices/cart/extraReducers/saveWpsPayment';
import { getTaxData } from '@src/global/store/slices/cart/extraReducers/getTaxData';
import i18n from '@src/global/localization/i18n';
import { IPayment, LOCALE_CODES } from '@src/global/types/types';
import { setUpdateBillingInformation } from '@src/global/store/slices/cart/cartSlice';
import { PaymentForm } from './form';
import { useTranslation } from 'react-i18next';

interface ReloadInitResponseContextType {
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  paymentErr?: string | null;
  setPaymentErr?: React.Dispatch<React.SetStateAction<string | null>>;
  paymentInitData?: paymentInfo;
  payment?: IPayment['payment'];
  setPayment?: React.Dispatch<React.SetStateAction<IPayment['payment']>>;
  paymentMethod?: string;
  setPaymentMethod?: React.Dispatch<React.SetStateAction<string>>;
}

export const ReloadInitResponseContext =
  createContext<ReloadInitResponseContextType>(null);

interface SecurePaymentProps {
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  handleOpenModal: () => void;
  onSubmit: () => void;
}

export const SecurePayment = ({
  isLoading,
  setIsLoading,
  handleOpenModal,
  onSubmit,
}: SecurePaymentProps) => {
  const language = i18n.language as LOCALE_CODES;
  const { t } = useTranslation();
  const { setShowNotification } = useNotification();

  const dispatch = useAppDispatch();
  const cartState = useSelector(cartSelector);

  const [reloadInitDataOnError, setReloadInitDataOnError] = useState(null);
  const [paymentErr, setPaymentErr] = useState<string | null>(null);
  const [mutateCreatePayment] = useMutation(CREATE_PAYMENT_INTENT);
  const [paymentInitData, setPaymentInitData] = useState<paymentInfo>();

  const [payment, setPayment] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('');

  useEffect(() => {
    const cartId = cartState.cartId;

    if (cartId && (reloadInitDataOnError === null || reloadInitDataOnError)) {
      setIsLoading(true);
      mutateCreatePayment({
        variables: {
          uid: cartId,
          local: process.env.REACT_APP_WPS_LOCAL_ENV === 'true',
        },
        fetchPolicy: 'no-cache',
        onCompleted(data: CREATE_PAYMENT_INTENT_TYPE) {
          setIsLoading(false);
          if (data.wInitPayment?.paymentInitData) {
            //:TODO remove after deleting frontend payment method selection option
            setPaymentMethod('Card');
            return setPaymentInitData(data.wInitPayment?.paymentInitData);
          }
          if (data.wInitPayment?.result === 'SUBSCRIPTION_EXISTS') {
            handleOpenModal();
          } else {
            setShowNotification([
              NotificationTypes.error,
              t('checkout.review.validationErr.errorToast'),
            ]);
          }
        },
      });
    }
    return () => {
      setIsLoading(false);
      setPaymentInitData(null);
      setReloadInitDataOnError(false);
      setPaymentMethod('');
    };
  }, [reloadInitDataOnError]);

  useEffect(() => {
    async function handleWPSwSavePaymentFunc() {
      setIsLoading(true);

      if (payment && cartState.updateBillingInformation === STATUSES.TRUE) {
        const wpsPaymentData = {
          paymentType: payment.paymentMethodName,
          payment: payment,
        };
        dispatch(updateWpsPaymentInformation({ wpsPaymentData })).then(
          (res) => {
            if (res.payload === 'OK') {
              setReloadInitDataOnError(false);
              setIsLoading(false);
              dispatch(getTaxData({ language }));
              analytic.dataLayerCheckOutPaymentBilling();
              onSubmit();
            }
            if (res.payload === 'ERROR') {
              setPayment(null);
              setPaymentInitData(null);
              setIsLoading(false);
              setPaymentErr(t('checkout.step2.validationErr.unableProcess'));
              dispatch(setUpdateBillingInformation(STATUSES.FALSE));
              setReloadInitDataOnError(true);
            }
          }
        );
      } else {
        setIsLoading(false);
      }
      return () => {
        setPayment(null);
        setPaymentInitData(null);
        setPaymentMethod('');
      };
    }

    handleWPSwSavePaymentFunc();
  }, [payment, cartState.updateBillingInformation]);

  return (
    <>
      {paymentInitData && !isLoading ? (
        <ReloadInitResponseContext.Provider
          value={{
            setIsLoading,
            paymentErr,
            setPaymentErr,
            paymentInitData,
            payment,
            setPayment,
            paymentMethod,
            setPaymentMethod,
          }}
        >
          <PaymentForm />
        </ReloadInitResponseContext.Provider>
      ) : (
        <div className="payment-form__spinner_box">
          <Spinner size={'64px'} />
        </div>
      )}
    </>
  );
};
